import { AlertService } from '@aurelia-toolkit/alert';
import { CombinedQueryInstrumentsResponseItem, InstrumentTypesCache, usingTraceAwareProgress } from '@dynamik/common';
import { PLATFORM } from 'aurelia-pal';
import { useView } from 'aurelia-templating';
import { Rule, ValidationController, ValidationControllerFactory, ValidationRules } from 'aurelia-validation';
import { validate } from '../../../decorators/validate';
import { InstrumentType } from '@dynamik/common/clients/policy-clients';
import { AvailableInstrument, FileTypes, Instrument, InstrumentCurrency, InstrumentsClient, InstrumentTypeViewModel, QueryInstrumentsResponseItem, QueryTransactionInstrumentsRequest, TransactionsClient, VoucherState } from '@dynamik/common/clients/transaction-clients';
import { AvailableInstrumentClient, QueryLookupInstrumentsRequest } from '@dynamik/common/clients/instrument-clients';
import { MdcDialog } from '@aurelia-mdc-web/dialog';
import { ICountry, countries } from 'countries-list';
import { IsinTaxableConfigurationsCache } from '../../../services/isin-taxable-configurations-cache';
import { CustomerService } from '@dynamik/common';
import { DEFAULT_GUID } from '../../../classes/default-guid';

export interface IEditInstrumentDialogModel {
  lookupAvailableInstrumentId?: string;
  transactionAvailableInstrumentId?: string;
  transactionInstrumentId?: string;
  instrument?: CombinedQueryInstrumentsResponseItem;
  templateVoucher?: VoucherState;
  isEditOfExistingInstrument?: boolean;
}

const undeterminedTaxableInstrumentTypes = [InstrumentType.Stocks, InstrumentType.EquityFunds, InstrumentType.ETF];
const taxableWarningInstrumentTypes = [InstrumentType.Bonds, InstrumentType.FixedIncomeFunds];

@useView(PLATFORM.moduleName('elements/dialogs/edit-instrument-dialog/edit-instrument-dialog.html'))
export class EditInstrumentDialog {
  constructor(public alertService: AlertService, private customerService: CustomerService, private availableInstrumentsClient: AvailableInstrumentClient,
    private instrumentsClient: InstrumentsClient, validationControllerFactory: ValidationControllerFactory,
    private transactionsClient: TransactionsClient, private instrumentTypesCache: InstrumentTypesCache, private itcCache: IsinTaxableConfigurationsCache) {
    this.validationController = validationControllerFactory.createForCurrentScope();
    this.rules = ValidationRules
      .ensureEx<CombinedQueryInstrumentsResponseItem, string>(x => x.name).required()
      .ensure(x => x.isin)
      /**/.required().when(x => !this.isNoted && !x.orgNr).withMessage('either ISIN or OrgNr must be set')
      /**/.satisfies((x, o) => this.instrumentsClient
        .search(this.customerService.selectedCustomer.companyId, new QueryTransactionInstrumentsRequest({ isin: x, companyIdentityId: this.customerService.selectedCustomer.companyId }))
        .then(x => !x.find(y => y.transactionAvailableInstrumentId !== o.transactionAvailableInstrumentId)))
      /**/.when(x => !!x.isin && !this.params.isEditOfExistingInstrument)
      /**/.withMessage('already exists')
      .ensure(x => x.orgNr).required().when(x => !this.isNoted && !x.isin).withMessage('either ISIN or OrgNr must be set')
      /**/.satisfies((x, o) => this.instrumentsClient
        .search(this.customerService.selectedCustomer.companyId, new QueryTransactionInstrumentsRequest({ orgNr: x, companyIdentityId: this.customerService.selectedCustomer.companyId }))
        .then(x => !x.find(y => y.transactionAvailableInstrumentId !== o.transactionAvailableInstrumentId)))
      /**/.when(x => !!x.orgNr)
      /**/.withMessage('already exists')
      .rules;
    this.instrumentRules = ValidationRules
      .ensureEx<Instrument, number>(x => x.instrumentTypeId).required()
            /**/.satisfies(async x => !this.instrument.transactionInstrumentId
        || this.transactionsClient.isNewInstrumentTypeValid(this.customerService.selectedCustomer.companyId, this.instrument.transactionInstrumentId, x))
            /**/.withMessage('not compatible with existing transactions')
      .rules;
  }

  dialog: MdcDialog;
  params: IEditInstrumentDialogModel;
  instrument: CombinedQueryInstrumentsResponseItem;
  validationController: ValidationController;
  rules: Rule<CombinedQueryInstrumentsResponseItem, unknown>[][];
  instrumentRules: Rule<Instrument, unknown>[][];
  instrumentTypes: InstrumentTypeViewModel[];
  customerInstrumentTypes: InstrumentType[];
  isNoted: boolean;
  isNew: boolean;
  countries = Object.getOwnPropertyNames(countries).map(x => ({ code: x, ...countries[x] as ICountry })).sort((a, b) => a.name.localeCompare(b.name));
  InstrumentType = InstrumentType;

  @usingTraceAwareProgress()
  async activate(params: IEditInstrumentDialogModel) {
    this.params = params;
    [this.instrument, this.instrumentTypes, this.customerInstrumentTypes] = await Promise.all([
      params.transactionAvailableInstrumentId
        ? await this.instrumentsClient.search(this.customerService.selectedCustomer.companyId, new QueryTransactionInstrumentsRequest({
          availableInstrumentId: params.transactionAvailableInstrumentId,
          companyIdentityId: this.customerService.selectedCustomer.companyId
        })).then(x => x[0])
        : params.lookupAvailableInstrumentId
          ? await this.availableInstrumentsClient.searchFuzzy(this.customerService.selectedCustomer.companyId, new QueryLookupInstrumentsRequest({
            availableInstrumentId: params.lookupAvailableInstrumentId,
            companyIdentityId: this.customerService.selectedCustomer.companyId
          })).then(x => x[0])
          : new QueryInstrumentsResponseItem({
            companyIdentityId: this.customerService.selectedCustomer.companyId,
            name: params.templateVoucher?.fileType === FileTypes.Voucher ? undefined : params.templateVoucher?.instrumentName,
            instrumentTypeId: params.templateVoucher?.importedInstrumentTypeId,
            isin: params.templateVoucher?.isin,
            orgNr: params.templateVoucher?.orgNr,
            currencyCode: params.templateVoucher?.instrument?.availableInstrument?.instrumentCurrency?.name ?? params.templateVoucher?.importedInstrumentCurrencyCode ?? 'NOK',
            countryCode: countries[params.templateVoucher?.isin?.substring(0, 1)] ?? 'NO'
          }),
      this.instrumentTypesCache.instrumentTypes,
      this.instrumentTypesCache.customerInstrumentTypes,
      this.itcCache.get()
    ]);
    this.instrumentTypes = this.instrumentTypes.filter(x => this.customerInstrumentTypes.includes(x.id as number) || this.instrument.instrumentTypeId === x.id);
    this.validationController.addObject(this.instrument, this.rules);
    this.isNoted = !!this.instrument.lookupAvailableInstrumentId;
    this.isNew = !params.lookupAvailableInstrumentId && !params.transactionAvailableInstrumentId;
  }

  @usingTraceAwareProgress()
  @validate(null)
   async save() {
     if (taxableWarningInstrumentTypes.includes(this.instrument.instrumentTypeId as number)
       && !this.instrument.instrumentTaxable
       && !await this.alertService.confirm(`Usually ${this.instrumentTypes.find(x => x.id === this.instrument.instrumentTypeId).name} are taxable. Are you sure you want to continue?`)) {
       return;
     }
    const request = new Instrument({
      uuid: this.instrument.transactionInstrumentId ?? DEFAULT_GUID,
      name: this.instrument.reportName,
      taxable: this.instrument.instrumentTaxable,
      longTerm: this.instrument.instrumentLongTerm,
      lowerOfCostOrMarket: this.instrument.instrumentLowerOfCostOrMarket,
      instrumentTypeId: this.instrument.instrumentTypeId as number,
      availableInstrument: new AvailableInstrument({
        uuid: this.instrument.transactionAvailableInstrumentId ?? DEFAULT_GUID,
        lookupAvailableInstrumentId: this.instrument.lookupAvailableInstrumentId,
        name: this.instrument.name,
        isin: this.instrument.isin,
        orgNr: this.instrument.orgNr,
        ticker: this.instrument.ticker,
        instrumentTypeId: this.instrument.instrumentTypeId as number,
        instrumentCurrency: new InstrumentCurrency({ name: this.instrument.currencyCode }),
        countryCode: this.instrument.countryCode
      })
    });
    const instrument = await this.instrumentsClient.post(this.customerService.selectedCustomer.companyId, request);
    this.instrument.transactionInstrumentId = instrument.uuid;
    this.instrument.transactionAvailableInstrumentId = instrument.availableInstrument.uuid;
    this.params.instrument = this.instrument;
    this.dialog.close('save');
  }

  isinChanged() {
    this.updateTaxable();
  }

  instrumentTypeChanged() {
    this.updateTaxable();
  }

  updateTaxable() {
    if (!undeterminedTaxableInstrumentTypes.includes(this.instrument.instrumentTypeId as number)) {
      this.instrument.instrumentTaxable = true;
      return;
    }
    if (!this.instrument.isin) {
      return;
    }
    this.instrument.instrumentTaxable = this.itcCache.isTaxable(this.instrument.isin);
  }

  longTermChanged() {
    this.instrument.instrumentLowerOfCostOrMarket = this.instrument.instrumentLowerOfCostOrMarket || this.instrument.instrumentLongTerm;
  }
}
